import { createContext, startTransition, useContext, useEffect, useMemo, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'system',
  resolvedTheme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // SSR: always start with 'system' so server and client initial render match.
  // Client will sync from localStorage in the effect below.
  const [theme, setThemeState] = useState<Theme>('system');
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>('light');

  // Sync from localStorage and matchMedia after hydration. This runs while the
  // lazy route's Suspense boundary is often still hydrating; an urgent context
  // update there throws React #421 and React client-renders the boundary from
  // scratch. As a transition, React finishes hydrating first, then applies it.
  // The <html> class is already correct from the pre-paint script in
  // index.html, so the deferred update causes no visible flash.
  useEffect(() => {
    const stored = (localStorage.getItem('ortem-theme') as Theme) || 'system';
    const system = getSystemTheme();
    startTransition(() => {
      setThemeState(stored);
      setSystemTheme(system);
    });
  }, []);

  // Listen for OS-level theme changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) =>
      startTransition(() => setSystemTheme(e.matches ? 'dark' : 'light'));
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme;

  // Apply class to <html>
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  // Stable value: a fresh object every render re-renders every consumer.
  const value = useMemo<ThemeContextValue>(() => {
    const setTheme = (t: Theme) => {
      localStorage.setItem('ortem-theme', t);
      setThemeState(t);
    };
    return {
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme: () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark'),
    };
  }, [theme, resolvedTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
